import { memo, useState } from "react"
import { useFormContext } from "react-hook-form"
import { isValidPhoneNumber } from "react-phone-number-input"
import * as Yup from "yup"

import useIsDesktop from "@hooks/useIsDesktop"
import useUser from "@hooks/useUser"

import { faIcon } from "@utils/faIcon"

import { Button } from "@atoms"
import { InputHelpTextProps } from "@atoms/InputHelpText/InputHelpText.types"

import { Dialog } from "@molecules"
import { PhoneNumberInput } from "@molecules/Form/Form"
import GenericForm from "@molecules/Form/GenericForm/GenericForm"
import { MutationSuccessFunctionParams } from "@molecules/Form/GenericForm/GenericForm.types"
import toast from "@molecules/Toast/Toast"

import { SecurityCheckDialog } from "@organisms/index"

import { USER_ENDPOINT } from "@endpoints/user"

import styles from "./PhoneFlowDialog.module.scss"
import type {
    PhoneChangeErrorResponse,
    PhoneFlowDialogProps,
    PhoneFlowInputProps,
    PhoneFlowSubmitButtonProps,
    UserPhone,
} from "./PhoneFlowDialog.types"

const UNCHANGED_PHONE_HELP_TEXT: InputHelpTextProps = {
    message: "This is your current phone number.",
    status: "info",
    showIcon: true,
}

const formSchema: Yup.ObjectSchema<UserPhone> = Yup.object().shape({
    phone: Yup.string()
        .required("Please enter a phone number")
        .test("is-valid-phone", "Please enter a valid phone number", (value) => isValidPhoneNumber(value)),
})

function PhoneChangeInput(props: PhoneFlowInputProps) {
    const { userPhone, defaultCountryCode } = props
    const { watch } = useFormContext<UserPhone>()
    const currentPhone = watch("phone")

    const isUnchanged = !!userPhone && currentPhone === userPhone

    return (
        <PhoneNumberInput<UserPhone>
            label={userPhone ? "New phone number" : "Phone number"}
            size="md"
            placeholder="(000) 000-0000"
            aria-label={userPhone ? "New phone number" : "Phone number"}
            defaultCountryCode={defaultCountryCode}
            name="phone"
            autoFocus={true}
            inputHelpText={isUnchanged ? UNCHANGED_PHONE_HELP_TEXT : undefined}
        />
    )
}

function SubmitButton(props: PhoneFlowSubmitButtonProps) {
    const { userPhone, isPhoneVerified, isPending } = props
    const { watch } = useFormContext<UserPhone>()
    const currentPhone = watch("phone")

    const isUnchangedAndVerified = currentPhone === userPhone && isPhoneVerified

    return (
        <Button variant="solid" size="lg" type="submit" isProcessing={isPending} noDebounce={true}>
            {isUnchangedAndVerified ? "Save" : "Send code"}
        </Button>
    )
}

function PhoneFlowDialog(props: PhoneFlowDialogProps) {
    const { children, defaultOpen = false } = props

    const { user, updateUser } = useUser()
    const isDesktop = useIsDesktop()

    const [isChangePhoneDialogOpen, setIsChangePhoneDialogOpen] = useState<boolean>(defaultOpen)
    const [isVerifyingPhone, setIsVerifyingPhone] = useState<boolean>(false)
    const [isSecurityCheckOpen, setIsSecurityCheckOpen] = useState<boolean>(false)

    const handleTriggerClick = () => {
        if (user?.phone && !user?.phone_verified) {
            setIsSecurityCheckOpen(true)
        } else {
            setIsChangePhoneDialogOpen(true)
        }
    }

    const handleVerificationSuccess = () => {
        updateUser({ phone_verified: true })

        setIsSecurityCheckOpen(false)
        setIsVerifyingPhone(false)

        toast({
            variant: "default",
            title: "Phone number verified",
            type: "success",
        })
    }

    const handleChangePhoneSuccess = (args: MutationSuccessFunctionParams<UserPhone, User>) => {
        const phoneWasChanged = user?.phone !== args.formValues.phone

        setIsChangePhoneDialogOpen(false)

        if (phoneWasChanged) {
            setIsVerifyingPhone(true)
            setIsSecurityCheckOpen(true)

            updateUser({
                phone: args.formValues.phone,
                phone_verified: false,
            })
        } else if (!user?.phone_verified) {
            // Unchanged but unverified - open verification without setting isVerifyingPhone
            // so it uses "verify" entry point instead of "change"
            setIsSecurityCheckOpen(true)
        }
    }

    const handleSecurityCheckLeftAction = () => {
        setIsSecurityCheckOpen(false)
        setIsChangePhoneDialogOpen(true)
    }

    const handleSecurityCheckDiscard = () => {
        setIsSecurityCheckOpen(false)
        setIsVerifyingPhone(false)
    }

    return (
        <>
            {children({ onClick: handleTriggerClick })}

            <Dialog.Root
                isOpen={isChangePhoneDialogOpen}
                onOpenChange={setIsChangePhoneDialogOpen}
                mode="dialog"
                variant="default"
            >
                <Dialog.Content>
                    <GenericForm<UserPhone, User, PhoneChangeErrorResponse, UserPhone>
                        formConfig={{
                            defaultValues: {
                                phone: "",
                            },
                        }}
                        formValidationSchema={formSchema}
                        mutationConfig={{
                            genericErrorMessage: "Could not update phone number",
                            method: "PATCH",
                            endpoint: USER_ENDPOINT,
                        }}
                        onMutationSuccess={handleChangePhoneSuccess}
                    >
                        {({ isPending }) => (
                            <>
                                <Dialog.Header.Root>
                                    <Dialog.Header.Title
                                        title={user?.phone ? "Change phone number" : "Set phone number"}
                                    />
                                    {isDesktop && <Dialog.Header.CloseButton />}
                                </Dialog.Header.Root>
                                <Dialog.Body className={styles.dialogBody}>
                                    <span className={styles.dialogBodyText}>
                                        {user?.phone
                                            ? "Enter a new phone number. We'll send a confirmation code to verify it."
                                            : "Enter phone number. We'll send a confirmation code to verify it."}
                                    </span>

                                    <PhoneChangeInput
                                        userPhone={user?.phone}
                                        defaultCountryCode={user?.service_company?.phone_number_country}
                                    />
                                </Dialog.Body>
                                <Dialog.Footer.Root>
                                    <Button
                                        variant="ghost"
                                        size="lg"
                                        onClick={() => setIsChangePhoneDialogOpen(false)}
                                    >
                                        Cancel
                                    </Button>
                                    <SubmitButton
                                        userPhone={user?.phone}
                                        isPhoneVerified={user?.phone_verified}
                                        isPending={isPending}
                                    />
                                </Dialog.Footer.Root>
                            </>
                        )}
                    </GenericForm>
                </Dialog.Content>
            </Dialog.Root>
            <SecurityCheckDialog
                checkFor="phone"
                onSuccess={handleVerificationSuccess}
                onOpenChange={setIsSecurityCheckOpen}
                isOpen={isSecurityCheckOpen}
                onDiscard={handleSecurityCheckDiscard}
                title="Verify phone number"
                entryPoint={isVerifyingPhone ? "change" : "verify"}
                leftActionOverride={
                    <Button
                        size="lg"
                        variant="ghost"
                        leftIcon={isVerifyingPhone ? faIcon({ name: "arrow-left", style: "regular" }) : undefined}
                        onClick={handleSecurityCheckLeftAction}
                    >
                        {isVerifyingPhone ? "Back" : "Correct phone"}
                    </Button>
                }
            />
        </>
    )
}

export default memo(PhoneFlowDialog)
